import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { DollarSign, TrendingUp, ShoppingBag, Download, Percent, X } from "lucide-react";
import { toast } from "sonner";
import { DatePresetPills } from "@/components/DatePresetPills";

/**
 * Admin · Bookkeeping
 *
 * Revenue, COGS, gross margin, and top SKUs from approved orders. Supports
 * one-click preset ranges (This month / Last month / YTD / Last 30d) and a
 * fully custom From/To range. All 3 CSV exports (Orders, Loyalty, P&L)
 * honour the active date range so accountants get an exact-slice pack.
 */
export default function Bookkeeping() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // If no custom range is set, default to Month-to-date on first paint so the
  // page always shows something meaningful.
  useEffect(() => {
    const today = new Date();
    const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1))
      .toISOString().slice(0, 10);
    const monthEnd = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0))
      .toISOString().slice(0, 10);
    setDateFrom(monthStart);
    setDateTo(monthEnd);
  }, []);

  const params = useMemo(() => {
    const p = {};
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (!dateFrom && !dateTo) return;
    setLoading(true);
    api.get("/admin/bookkeeping/summary", { params }).then((r) => {
      setSummary(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [params, dateFrom, dateTo]);

  const rangeLabel = summary?.period === "custom"
    ? `${dateFrom || "—"} to ${dateTo || "—"}`
    : summary?.period === "last_month" ? "Last month"
    : summary?.period === "ytd" ? "Year-to-date"
    : summary?.period === "30d" ? "Last 30 days"
    : "Month-to-date";

  const download = async (path, defaultName, extra = {}) => {
    try {
      const r = await api.get(path, { params: { ...params, ...extra }, responseType: "blob" });
      const disposition = r.headers?.["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/.exec(disposition);
      const filename = match ? match[1] : defaultName;
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Download failed.");
    }
  };

  const clearRange = () => { setDateFrom(""); setDateTo(""); };

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10" data-testid="admin-bookkeeping">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="label-eyebrow">Books</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1">Bookkeeping</h1>
          <p className="text-sm text-[#5E636E] mt-1">Revenue, COGS, gross margin and top SKUs from approved orders.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => download("/admin/exports/orders.csv", "nebula-orders.csv")} className="btn-secondary inline-flex items-center gap-2" data-testid="export-orders-btn">
            <Download className="h-4 w-4" /> Orders CSV
          </button>
          <button onClick={() => download("/admin/loyalty/ledger.csv", "nebula-loyalty-ledger.csv")} className="btn-secondary inline-flex items-center gap-2" data-testid="export-loyalty-btn">
            <Download className="h-4 w-4" /> Loyalty CSV
          </button>
          <button onClick={() => download("/admin/exports/pnl.csv", "nebula-pnl.csv")} className="btn-primary inline-flex items-center gap-2" data-testid="export-pnl-btn">
            <Download className="h-4 w-4" /> P&L CSV
          </button>
        </div>
      </div>

      {/* Date range controls — presets + custom inputs. */}
      <div className="mt-6 border border-[#D1D5DB] rounded-md bg-[#F7F7F8] p-4 flex flex-col md:flex-row md:items-end gap-3" data-testid="bk-range-controls">
        <div>
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#5E636E] mb-1.5">Preset</label>
          <DatePresetPills
            from={dateFrom}
            to={dateTo}
            onChange={(f, t) => { setDateFrom(f); setDateTo(t); }}
            includeLast30
          />
        </div>
        <div className="md:ml-4">
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#5E636E] mb-1.5">From</label>
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="px-3 py-2 rounded-md border border-[#D1D5DB] bg-white text-sm outline-none focus:border-[#0B0B1F]"
            data-testid="bk-range-from"
          />
        </div>
        <div>
          <label className="block font-mono text-[10.5px] uppercase tracking-[0.16em] text-[#5E636E] mb-1.5">To</label>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="px-3 py-2 rounded-md border border-[#D1D5DB] bg-white text-sm outline-none focus:border-[#0B0B1F]"
            data-testid="bk-range-to"
          />
        </div>
        {(dateFrom || dateTo) && (
          <button
            onClick={clearRange}
            className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-[#D1D5DB] text-[#5E636E] hover:text-[#0B0B1F] hover:border-[#0B0B1F] transition text-[13px]"
            data-testid="bk-range-clear"
          >
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8" data-testid="bk-cards">
        <Card icon={DollarSign} label="Revenue" value={loading ? "…" : fmt(summary?.revenue)} accent="#003366" />
        <Card icon={ShoppingBag} label="Orders" value={loading ? "…" : (summary?.orders_count ?? 0)} accent="#0B0B1F" />
        <Card icon={TrendingUp} label="Gross margin" value={loading ? "…" : fmt(summary?.gross_margin)} accent="#0B6623" />
        <Card icon={Percent} label="Margin %" value={loading ? "…" : `${summary?.gross_margin_pct ?? 0}%`} accent="#0B6623" />
      </div>

      <div className="mt-10 border border-[#D1D5DB]">
        <div className="px-5 py-3 bg-[#F7F7F8] border-b border-[#D1D5DB] font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">
          Top SKUs · {rangeLabel}
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white">
            <tr className="border-b border-[#E7E8EA]">
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">SKU</th>
              <th className="text-left px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">Product</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">Units</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">Revenue</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">COGS</th>
              <th className="text-right px-4 py-3 font-mono text-[11px] tracking-[0.18em] uppercase text-[#5E636E]">Gross</th>
            </tr>
          </thead>
          <tbody>
            {!loading && (summary?.top_skus || []).length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-[#5E636E]">No approved orders in this range.</td></tr>
            )}
            {!loading && (summary?.top_skus || []).map((r) => (
              <tr key={r.sku} className="border-t border-[#E7E8EA] hover:bg-[#F7F7F8]" data-testid={`bk-row-${r.sku}`}>
                <td className="px-4 py-3 font-mono text-[12px]">{r.sku}</td>
                <td className="px-4 py-3">{r.product_name}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px]">{r.units_sold}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px]">{fmt(r.revenue)}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-[#5E636E]">{fmt(r.cogs)}</td>
                <td className="px-4 py-3 text-right font-mono text-[12px] text-[#0B6623]">{fmt(r.gross_margin)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-[#D1D5DB] p-5 bg-white">
      <Icon className="h-5 w-5" strokeWidth={1.6} style={{ color: accent }} />
      <div className="font-mono text-[10.5px] tracking-[0.18em] uppercase text-[#5E636E] mt-3">{label}</div>
      <div className="font-mono text-3xl font-semibold mt-1" style={{ color: accent }}>{value}</div>
    </div>
  );
}
